import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import MicButton from "@/components/MicButton";
import MARGrid from "@/components/MARGrid";
import ClockButton from "@/components/ClockButton";
import VitalsPanel from "@/components/VitalsPanel";
import WellnessPanel from "@/components/WellnessPanel";
import CaregiverFormsPanel from "@/components/CaregiverFormsPanel";
import VoiceFillModal from "@/components/VoiceFillModal";
import VoiceFamilyUpdateModal from "@/components/VoiceFamilyUpdateModal";
import VoiceShiftHandoffModal from "@/components/VoiceShiftHandoffModal";
import IncomingHandoffBanner from "@/components/IncomingHandoffBanner";
import EvvCheckInCard from "@/components/EvvCheckInCard";
import PhotoAttachments from "@/components/PhotoAttachments";
import QuickContactsEditor from "@/components/QuickContactsEditor";
import HandoffBriefingCard from "@/components/shift-report/HandoffBriefingCard";
import CareChecklist from "@/components/shift-report/CareChecklist";
import PreviewSubmitModal from "@/components/shift-report/PreviewSubmitModal";
import { toast } from "sonner";
import { loadDraft, saveDraft, clearDraft, submitOrQueue } from "@/lib/drafts";
import { track } from "@/lib/sentry";
import {
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  Circle,
  Loader2,
  Send,
  AlertTriangle,
  Utensils,
  Pill,
  Footprints,
  ShowerHead,
  Smile,
  Droplets,
  History,
  Eye,
  Pencil,
  Clock4,
} from "lucide-react";

const CHECK_ITEMS = [
  { key: "meals", label: "Meals", icon: Utensils },
  { key: "medication", label: "Medication", icon: Pill },
  { key: "mobility", label: "Mobility", icon: Footprints },
  { key: "hygiene", label: "Hygiene", icon: ShowerHead },
  { key: "behavior", label: "Behavior", icon: Smile },
  { key: "hydration", label: "Hydration", icon: Droplets },
];

const SHIFT_TYPES = ["morning", "afternoon", "evening", "night"];

export default function ShiftReport() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formatting, setFormatting] = useState(false);

  const [shiftType, setShiftType] = useState("morning");
  const [checklist, setChecklist] = useState(
    Object.fromEntries(CHECK_ITEMS.map((i) => [i.key, false]))
  );
  const [rawNotes, setRawNotes] = useState("");
  const [formattedNotes, setFormattedNotes] = useState("");
  const [incident, setIncident] = useState(false);

  // EVV + handoff
  const [activeShift, setActiveShift] = useState(null);
  const [handoff, setHandoff] = useState(null);
  const [handoffLoading, setHandoffLoading] = useState(true);
  const [showHandoff, setShowHandoff] = useState(true);
  const [lastSubmittedId, setLastSubmittedId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [pingingLate, setPingingLate] = useState(false);
  // Voice fill (P1 AI flow)
  const [voiceFillOpen, setVoiceFillOpen] = useState(false);
  const [voiceFamilyOpen, setVoiceFamilyOpen] = useState(false);
  const [voiceHandoffOpen, setVoiceHandoffOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const openPreview = () => {
    if (!rawNotes.trim()) {
      toast.error("Notes are required");
      return;
    }
    setShowPreview(true);
  };

  const pingLate = async () => {
    const etaStr = window.prompt("ETA in minutes (e.g. 15). Leave blank if unknown:");
    if (etaStr === null) return; // cancelled
    const eta = etaStr.trim() === "" ? null : Math.max(0, Math.min(600, parseInt(etaStr, 10) || 0));
    setPingingLate(true);
    try {
      await api.post("/me/late-arrival", {
        client_id: clientId,
        eta_minutes: eta,
        note: "",
      });
      toast.success("Admin notified you're running late");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not notify admin");
    } finally {
      setPingingLate(false);
    }
  };

  const refreshActive = useCallback(async () => {
    try {
      const { data } = await api.get("/shifts/active");
      setActiveShift(data.active);
    } catch {
      setActiveShift(null);
    }
  }, []);

  useEffect(() => {
    api
      .get("/clients")
      .then(({ data }) => {
        const c = data.find((x) => x.id === clientId);
        setClient(c || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    refreshActive();
    setHandoffLoading(true);
    api
      .get(`/clients/${clientId}/handoff`)
      .then(({ data }) => setHandoff(data))
      .catch(() => setHandoff(null))
      .finally(() => setHandoffLoading(false));
    track("report_started", { client_id: clientId });
  }, [clientId, refreshActive]);

  // Load draft (if any) — only once on mount.
  useEffect(() => {
    const d = loadDraft(clientId);
    if (d) {
      setShiftType(d.shiftType || "morning");
      setChecklist(
        d.checklist ||
          Object.fromEntries(CHECK_ITEMS.map((i) => [i.key, false]))
      );
      setRawNotes(d.rawNotes || "");
      setFormattedNotes(d.formattedNotes || "");
      setIncident(!!d.incident);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save draft on changes (debounced).
  useEffect(() => {
    const t = setTimeout(() => {
      if (rawNotes || formattedNotes || incident || Object.values(checklist).some(Boolean)) {
        saveDraft(clientId, { shiftType, checklist, rawNotes, formattedNotes, incident });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [clientId, shiftType, checklist, rawNotes, formattedNotes, incident]);

  const checklistContext = useMemo(() => {
    const done = CHECK_ITEMS.filter((i) => checklist[i.key]).map((i) => i.label);
    if (!done.length) return "";
    return `Completed tasks: ${done.join(", ")}.`;
  }, [checklist]);

  const toggle = (key) =>
    setChecklist((c) => ({ ...c, [key]: !c[key] }));

  const onTranscribed = (text) => {
    setRawNotes((prev) => (prev ? prev.trim() + " " + text : text));
  };

  /**
   * Apply voice-fill payload returned by VoiceFillModal.
   *
   * Direct ShiftReport state (checklist / notes / shift_type / incident) is
   * merged into local state synchronously. Other sections (vitals / mood /
   * sleep / fluid / daily_activity) are POSTed to their backend endpoints
   * immediately so the sub-panels show them as committed entries; the panels
   * are re-keyed to force a refetch.
   */
  const applyVoiceFill = async (payload) => {
    // ---- Direct local-state merges ----
    if (payload.shift) {
      const s = payload.shift;
      if (s.shift_type && SHIFT_TYPES.includes(s.shift_type)) setShiftType(s.shift_type);
      if (s.raw_notes) {
        setRawNotes((prev) => (prev ? prev.trim() + "\n\n" + s.raw_notes : s.raw_notes));
      } else if (payload._transcript) {
        setRawNotes((prev) => (prev ? prev.trim() + "\n\n" + payload._transcript : payload._transcript));
      }
      if (s.formatted_notes) setFormattedNotes(s.formatted_notes);
      if (s.incident_flag) setIncident(true);
    }
    if (payload.checklist) {
      setChecklist((prev) => {
        const next = { ...prev };
        Object.entries(payload.checklist).forEach(([k, v]) => {
          if (v === true) next[k] = true;
        });
        return next;
      });
    }

    // ---- Background POSTs for sub-forms ----
    const tasks = [];
    const today = new Date().toISOString().slice(0, 10);
    if (payload.vitals) {
      const v = payload.vitals;
      const hasAny = ["systolic", "diastolic", "pulse", "temp_f",
        "weight_lb", "blood_sugar", "o2_sat"].some((k) => v[k] != null);
      if (hasAny) {
        tasks.push(
          api.post(`/clients/${clientId}/vitals`, {
            systolic: v.systolic ?? null,
            diastolic: v.diastolic ?? null,
            pulse: v.pulse ?? null,
            temp_f: v.temp_f ?? null,
            weight_lb: v.weight_lb ?? null,
            blood_sugar: v.blood_sugar ?? null,
            o2_sat: v.o2_sat ?? null,
            notes: v.notes || "",
          }).catch((e) => {
            // eslint-disable-next-line no-console
            console.warn("voice-fill: vitals POST failed", e?.response?.data);
          })
        );
      }
    }
    if (payload.mood && payload.mood.rating != null) {
      const moodShift = String(payload.mood.shift || shiftType).toLowerCase();
      const safeShift = ["morning", "afternoon", "evening", "night"].includes(moodShift)
        ? moodShift : shiftType;
      tasks.push(
        api.post(`/clients/${clientId}/mood`, {
          shift: safeShift,
          rating: Math.max(-3, Math.min(3, parseInt(payload.mood.rating, 10) || 0)),
        }).catch((e) => {
          // eslint-disable-next-line no-console
          console.warn("voice-fill: mood POST failed", e?.response?.data);
        })
      );
    }
    if (payload.sleep && (payload.sleep.quality || payload.sleep.total_hours != null)) {
      // Map total_hours to a continuous_block bucket so the backend's
      // strict Literal doesn't 422. ≥4h is the most common case.
      let cb = null;
      const h = payload.sleep.total_hours;
      if (h != null) {
        if (h < 1) cb = "<1h";
        else if (h < 2) cb = "1-2h";
        else if (h < 4) cb = "2-4h";
        else cb = "4+h";
      }
      const body = {
        sleep_date: today,
        quality: payload.sleep.quality || "fair",
        ...(payload.sleep.total_hours != null ? { total_hours: payload.sleep.total_hours } : {}),
        ...(cb ? { continuous_block: cb } : {}),
      };
      tasks.push(
        api.post(`/clients/${clientId}/sleep`, body).catch((e) => {
          // Surface so future regressions are visible without breaking the flow
          // eslint-disable-next-line no-console
          console.warn("voice-fill: sleep POST failed", e?.response?.data);
        })
      );
    }
    if (Array.isArray(payload.fluid)) {
      payload.fluid.forEach((f) => {
        if (!f) return;
        tasks.push(
          api.post(`/clients/${clientId}/fluid`, {
            fluid_type: f.fluid_type || "water",
            amount_ml: f.amount_ml ?? 0,
            refused: !!f.refused,
          }).catch((e) => {
            // eslint-disable-next-line no-console
            console.warn("voice-fill: fluid POST failed", e?.response?.data);
          })
        );
      });
    }
    if (payload.daily_activity) {
      const d = payload.daily_activity;
      const hasAny = ["bathing", "dressing", "grooming", "toileting", "eating",
        "social_engagement", "appetite"].some((k) => d[k]) ||
        (d.activities || []).length > 0;
      if (hasAny) {
        tasks.push(
          api.post(`/clients/${clientId}/daily-activity`, {
            activity_date: today,
            bathing: d.bathing || "assist",
            dressing: d.dressing || "assist",
            grooming: d.grooming || "assist",
            toileting: d.toileting || "assist",
            eating: d.eating || "assist",
            social_engagement: d.social_engagement || "moderate",
            activities: d.activities || [],
            appetite: d.appetite || "good",
          }).catch((e) => {
            // eslint-disable-next-line no-console
            console.warn("voice-fill: daily-activity POST failed", e?.response?.data);
          })
        );
      }
    }
    if (tasks.length > 0) {
      await Promise.all(tasks);
      // Bump refresh key so VitalsPanel / WellnessPanel / CaregiverFormsPanel
      // remount and re-fetch their latest entries.
      setRefreshKey((k) => k + 1);
    }
    track("voice_fill_applied", {
      client_id: clientId,
      sections: Object.keys(payload).filter((k) => k !== "_transcript"),
      sub_form_writes: tasks.length,
    });
  };

  const formatWithAI = async () => {
    if (!rawNotes.trim()) {
      toast.error("Write or dictate notes first");
      return;
    }
    setFormatting(true);
    try {
      const { data } = await api.post("/ai/format-note", {
        raw_notes: rawNotes,
        context: `Client: ${client?.name}. Shift: ${shiftType}. ${checklistContext}`,
      });
      setFormattedNotes(data.formatted_notes);
      toast.success("Formatted by AI");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI formatting failed");
    } finally {
      setFormatting(false);
    }
  };

  const submit = async () => {
    if (!rawNotes.trim()) {
      toast.error("Notes are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client_id: clientId,
        shift_date: new Date().toISOString().slice(0, 10),
        shift_type: shiftType,
        checklist,
        raw_notes: rawNotes,
        formatted_notes: formattedNotes || rawNotes,
        incident_flag: incident,
      };
      const isClockedHere = activeShift && activeShift.client_id === clientId;
      const url = isClockedHere ? "/reports?clock_out=1" : "/reports";
      const res = await submitOrQueue(payload, client?.name || "", url);
      clearDraft(clientId);
      setShowPreview(false);
      if (res.queued) {
        toast.success("You're offline — report saved and will sync automatically");
        navigate("/my-reports");
      } else {
        toast.success(isClockedHere ? "Shift report submitted · clocked out" : "Shift report submitted");
        if (isClockedHere) setActiveShift(null);
        if (res.data?.id) {
          // Stay on page so caregiver can attach photos to the just-saved report
          setLastSubmittedId(res.data.id);
        } else {
          navigate("/my-reports");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!client) {
    return (
      <div className="card-surface p-8 text-center">
        <h3 className="font-heading text-lg">Client not found</h3>
        <button onClick={() => navigate("/clients")} className="btn-secondary mt-4">
          Back to clients
        </button>
      </div>
    );
  }

  // Post-submit success view — lets caregiver attach photos to the report they just created.
  if (lastSubmittedId) {
    return (
      <div className="space-y-6 md:max-w-3xl md:mx-auto" data-testid="shift-report-submitted">
        <div className="card-surface p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-lg font-semibold">Shift report submitted</h2>
              <p className="mt-1 text-sm text-clinical-subtext">
                {client.name} · {new Date().toLocaleDateString()}. Attach photos below
                (wound progression, meal plate, env. hazard) or finish.
              </p>
            </div>
          </div>
        </div>

        <PhotoAttachments reportId={lastSubmittedId} />

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => navigate("/my-reports")}
            className="btn-secondary"
            data-testid="post-submit-my-reports"
          >
            View my reports
          </button>
          <button
            onClick={() => navigate("/clients")}
            className="btn-primary"
            data-testid="post-submit-done"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:max-w-3xl md:mx-auto" data-testid="shift-report-page">
      <button
        onClick={() => navigate("/clients")}
        className="btn-ghost -ml-3"
        data-testid="back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="label-eyebrow">Shift report</div>
        <h1 className="mt-1 text-2xl font-heading font-semibold">{client.name}</h1>
        {client.notes && (
          <p className="mt-1 text-sm text-clinical-subtext">{client.notes}</p>
        )}
      </div>

      {/* Clock-in / out */}
      {activeShift && activeShift.client_id === clientId ? (
        <ClockButton
          clientId={clientId}
          active={activeShift}
          onChange={(next) => { setActiveShift(next); refreshActive(); }}
          variant="full"
        />
      ) : (
        <ClockButton
          clientId={clientId}
          active={activeShift}
          onChange={(next) => { setActiveShift(next); refreshActive(); }}
          variant="full"
        />
      )}

      {/* Running-late ping (own bar) */}
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={pingLate}
          disabled={pingingLate}
          className="btn-ghost text-clinical-accent"
          data-testid="late-arrival-button"
          title="Notify admin you are running late"
        >
          <Clock4 className="h-4 w-4" />
          {pingingLate ? "Sending…" : "Running late"}
        </button>
      </div>

      {/* Quick contacts — caregiver-editable */}
      <QuickContactsEditor clientId={clientId} emergencyNote={client.emergency_note} />

      {/* GPS check-in / check-out for EVV (only renders if ?visit_id= present) */}
      <EvvCheckInCard clientId={clientId} />

      {/* Incoming voice handoff from previous caregiver (last 36h, unacked) */}
      <IncomingHandoffBanner clientId={clientId} />

      {/* Shift handoff briefing */}
      <HandoffBriefingCard
        handoffLoading={handoffLoading}
        handoff={handoff}
        showHandoff={showHandoff}
        onToggle={() => setShowHandoff((v) => !v)}
      />

      {/* Shift Type */}
      <div className="card-surface p-4">
        <div className="label-eyebrow mb-2">Shift</div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {SHIFT_TYPES.map((s) => (
            <button
              key={s}
              onClick={() => setShiftType(s)}
              data-testid={`shift-type-${s}`}
              className={`shrink-0 rounded-lg px-3 py-2 text-sm capitalize transition-all ${
                shiftType === s
                  ? "bg-clinical-primary text-white"
                  : "bg-clinical-bg text-clinical-subtext hover:text-clinical-text"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* AI Voice Fill — describe the shift, AI fills 8 forms at once */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setVoiceFillOpen(true)}
          className="card-surface p-4 text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-clinical-accent"
          style={{ background: "linear-gradient(135deg, #7C3AED12 0%, #0EA5E912 100%)", borderColor: "#7C3AED40" }}
          data-testid="voice-fill-open"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{ background: "#7C3AED", color: "white" }}>
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-clinical-text">AI Voice Fill</div>
              <p className="text-xs text-clinical-subtext">
                Talk through your shift — AI fills vitals, mood, sleep, fluid, activity & notes.
              </p>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setVoiceFamilyOpen(true)}
          className="card-surface p-4 text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-pink-500"
          style={{ background: "linear-gradient(135deg, #EC489912 0%, #F97316/15 100%)", borderColor: "#EC489940" }}
          data-testid="voice-family-update-open"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{ background: "#EC4899", color: "white" }}>
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-clinical-text">Voice update to family</div>
              <p className="text-xs text-clinical-subtext">
                Talk casually — AI writes a warm message for the family portal.
              </p>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setVoiceHandoffOpen(true)}
          className="card-surface p-4 text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-cyan-500 sm:col-span-2"
          style={{ background: "linear-gradient(135deg, #06B6D412 0%, #0EA5E915 100%)", borderColor: "#06B6D440" }}
          data-testid="voice-shift-handoff-open"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
              style={{ background: "#0891B2", color: "white" }}>
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-clinical-text">Voice handoff for next caregiver</div>
              <p className="text-xs text-clinical-subtext">
                30-90 second brief — AI extracts key points, concerns, and to-dos.
              </p>
            </div>
          </div>
        </button>
      </div>

      {/* MAR — today's medications */}
      <div className="card-surface p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-eyebrow">Today's medications</div>
          <span className="text-[11px] text-clinical-subtext">Tap to log</span>
        </div>
        <MARGrid clientId={clientId} />
      </div>

      {/* Vitals */}
      <VitalsPanel clientId={clientId} key={`vitals-${refreshKey}`} />

      {/* Wellness — mood / fluid / sleep / bowel + incident reporting */}
      <WellnessPanel clientId={clientId} key={`wellness-${refreshKey}`} />

      {/* Additional clinical forms — body check, vomiting, daily activity,
          hospital transfer, appointments, critical incidents */}
      <CaregiverFormsPanel clientId={clientId} key={`forms-${refreshKey}`} />

      {/* Checklist */}
      <CareChecklist items={CHECK_ITEMS} checklist={checklist} onToggle={toggle} />

      {/* Notes */}
      <div className="card-surface p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow">Your notes</div>
          <MicButton onTranscribed={onTranscribed} />
        </div>
        <textarea
          value={rawNotes}
          onChange={(e) => setRawNotes(e.target.value)}
          rows={5}
          placeholder="e.g., Client ate breakfast, walked with assistance, complained of knee pain..."
          className="input-clinical resize-y"
          data-testid="raw-notes-textarea"
        />
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <button
            onClick={formatWithAI}
            disabled={formatting || !rawNotes.trim()}
            className="btn-secondary flex-1"
            data-testid="format-ai-button"
          >
            {formatting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Formatting…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Format with AI
              </>
            )}
          </button>
          <button
            onClick={() => setIncident((v) => !v)}
            data-testid="incident-toggle"
            className={`btn-secondary ${
              incident ? "!border-destructive/50 !text-destructive" : ""
            }`}
          >
            <AlertTriangle className="h-4 w-4" />
            {incident ? "Incident flagged" : "Flag incident"}
          </button>
        </div>
      </div>

      {/* AI Output */}
      {formattedNotes && (
        <div
          className="rounded-2xl border border-clinical-accent/30 bg-clinical-accent/5 p-4"
          data-testid="ai-formatted-output"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-4 w-4 text-clinical-accent" />
            <div className="label-eyebrow !text-clinical-accent">AI Clinical Note</div>
          </div>
          <textarea
            value={formattedNotes}
            onChange={(e) => setFormattedNotes(e.target.value)}
            rows={Math.max(5, Math.min(14, formattedNotes.split("\n").length + 2))}
            className="w-full resize-y bg-transparent text-sm leading-relaxed text-clinical-text focus:outline-none"
            data-testid="formatted-notes-textarea"
          />
          <div className="mt-1 text-xs text-clinical-subtext">
            You can edit the formatted version before submitting.
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={openPreview}
        disabled={!rawNotes.trim() || submitting}
        className="btn-primary w-full"
        data-testid="preview-report-button"
      >
        <Eye className="h-4 w-4" /> Preview &amp; submit
      </button>

      {showPreview && (
        <PreviewSubmitModal
          client={client}
          shiftType={shiftType}
          checklist={checklist}
          items={CHECK_ITEMS}
          rawNotes={rawNotes}
          formattedNotes={formattedNotes}
          incident={incident}
          activeShift={activeShift}
          clientId={clientId}
          submitting={submitting}
          onClose={() => setShowPreview(false)}
          onSubmit={submit}
        />
      )}

      <VoiceFillModal
        open={voiceFillOpen}
        onClose={() => setVoiceFillOpen(false)}
        clientId={clientId}
        onApply={applyVoiceFill}
      />

      <VoiceFamilyUpdateModal
        open={voiceFamilyOpen}
        clientId={clientId}
        onClose={() => setVoiceFamilyOpen(false)}
      />

      <VoiceShiftHandoffModal
        open={voiceHandoffOpen}
        clientId={clientId}
        onClose={() => setVoiceHandoffOpen(false)}
      />
    </div>
  );
}
